import React, { useState, useMemo } from 'react';
import './BranchingNarrative.css';

interface HistoryEvent {
  id: string;
  year: number;
  category: string;
  description: string;
  significance: number;
  tags: string[];
  actors?: string[];
  location?: string;
  consequences?: string[];
}

interface HistoryEntity {
  id: string;
  type: string;
  name: string;
  createdYear: number;
  currentStatus: string;
  description?: string;
}

interface BranchingNarrativeProps {
  events: HistoryEvent[];
  entities: HistoryEntity[];
  startEvent: HistoryEvent | null;
  onEventSelect: (event: HistoryEvent) => void;
}

interface NarrativeNode {
  event: HistoryEvent;
  children: NarrativeNode[];
  parent: NarrativeNode | null;
  depth: number;
}

export const BranchingNarrative: React.FC<BranchingNarrativeProps> = ({
  events,
  entities,
  startEvent,
  onEventSelect,
}) => {
  const [selectedNode, setSelectedNode] = useState<NarrativeNode | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  // Build narrative tree from events
  const narrativeTree = useMemo(() => {
    if (!startEvent) return null;

    const buildNode = (event: HistoryEvent, depth: number, visited: Set<string>): NarrativeNode => {
      // Find events that could be consequences of this event
      const potentialConsequences = events.filter(e => {
        if (visited.has(e.id)) return false;
        if (e.year <= event.year) return false;
        
        // Check if this event mentions actors from the parent event
        const hasSharedActors = event.actors && e.actors && 
          event.actors.some(actor => e.actors!.includes(actor));
        
        // Check if description references the parent event
        const referencesParent = e.description.toLowerCase().includes(
          event.description.toLowerCase().substring(0, 20)
        );

        // Check if in same location
        const sameLocation = event.location && e.location && event.location === e.location;

        return hasSharedActors || referencesParent || sameLocation;
      }).slice(0, 3); // Limit to 3 branches per node

      const children: NarrativeNode[] = potentialConsequences.map(consequence => 
        buildNode(consequence, depth + 1, new Set(Array.from(visited).concat([consequence.id])))
      );

      return {
        event,
        children,
        parent: null,
        depth,
      };
    };

    return buildNode(startEvent, 0, new Set([startEvent.id]));
  }, [events, startEvent]);

  const toggleNode = (nodeId: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const renderNode = (node: NarrativeNode): React.ReactNode => {
    const isExpanded = expandedNodes.has(node.event.id);
    const isSelected = selectedNode?.event.id === node.event.id;

    return (
      <div key={node.event.id} className="narrative-node" style={{ marginLeft: `${node.depth * 40}px` }}>
        <div
          className={`node-card ${isSelected ? 'selected' : ''}`}
          onClick={() => {
            setSelectedNode(node);
            onEventSelect(node.event);
          }}
        >
          <div className="node-header">
            <div className="node-year">Year {node.event.year}</div>
            <div className="node-category">{node.event.category}</div>
            <div className="node-significance">★ {node.event.significance}</div>
          </div>
          
          <p className="node-description">{node.event.description}</p>
          
          {node.children.length > 0 && (
            <button
              className="node-expand-button"
              onClick={(e) => {
                e.stopPropagation();
                toggleNode(node.event.id);
              }}
            >
              {isExpanded ? '▼' : '▶'} {node.children.length} consequence{node.children.length > 1 ? 's' : ''}
            </button>
          )}
        </div>

        {isExpanded && node.children.map(child => renderNode(child))}
      </div>
    );
  };

  const getRelatedEntities = (event: HistoryEvent): HistoryEntity[] => {
    return entities.filter(entity =>
      event.actors?.includes(entity.id) ||
      event.description.includes(entity.name)
    );
  };

  const findSignificantStartingPoints = (): HistoryEvent[] => {
    return events
      .filter(e => e.significance >= 8)
      .sort((a, b) => b.significance - a.significance)
      .slice(0, 5);
  };

  return (
    <div className="branching-narrative">
      <div className="narrative-header">
        <h3>Branching Narrative</h3>
        <p className="narrative-subtitle">Explore how events cascade through history</p>
      </div>

      <div className="narrative-controls">
        <div className="starting-points">
          <label>Start from:</label>
          <select
            value={startEvent?.id || ''}
            onChange={(e) => {
              const selected = events.find(ev => ev.id === e.target.value);
              if (selected) onEventSelect(selected);
            }}
            className="narrative-select"
          >
            <option value="">Select a starting event...</option>
            {findSignificantStartingPoints().map(event => (
              <option key={event.id} value={event.id}>
                Year {event.year}: {event.description.substring(0, 50)}...
              </option>
            ))}
          </select>
        </div>

        <button
          className="expand-all-button"
          onClick={() => {
            const allNodeIds = new Set<string>();
            const collectIds = (node: NarrativeNode) => {
              allNodeIds.add(node.event.id);
              node.children.forEach(collectIds);
            };
            if (narrativeTree) collectIds(narrativeTree);
            setExpandedNodes(allNodeIds);
          }}
        >
          Expand All
        </button>

        <button
          className="collapse-all-button"
          onClick={() => setExpandedNodes(new Set())}
        >
          Collapse All
        </button>
      </div>

      <div className="narrative-content">
        {!startEvent ? (
          <div className="empty-state">
            <div className="empty-icon">📖</div>
            <h4>Select a starting event</h4>
            <p>Choose a significant event to begin exploring its narrative branches</p>
          </div>
        ) : (
          <>
            <div className="narrative-tree">
              {narrativeTree && renderNode(narrativeTree)}
            </div>

            {selectedNode && (
              <div className="narrative-detail-panel">
                <div className="detail-panel-header">
                  <h4>Event Details</h4>
                  <button
                    className="close-button"
                    onClick={() => setSelectedNode(null)}
                  >
                    ✕
                  </button>
                </div>

                <div className="detail-panel-content">
                  <div className="detail-row">
                    <span className="label">Year:</span>
                    <span className="value">{selectedNode.event.year}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Category:</span>
                    <span className="value">{selectedNode.event.category}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Significance:</span>
                    <span className="value">★ {selectedNode.event.significance}/10</span>
                  </div>
                  <div className="detail-row full-width">
                    <span className="label">Description:</span>
                    <p className="value">{selectedNode.event.description}</p>
                  </div>

                  {selectedNode.event.location && (
                    <div className="detail-row">
                      <span className="label">Location:</span>
                      <span className="value">{selectedNode.event.location}</span>
                    </div>
                  )}

                  {selectedNode.event.tags.length > 0 && (
                    <div className="detail-row full-width">
                      <span className="label">Tags:</span>
                      <div className="tags-list">
                        {selectedNode.event.tags.map(tag => (
                          <span key={tag} className="tag">{tag}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {getRelatedEntities(selectedNode.event).length > 0 && (
                    <div className="detail-row full-width">
                      <span className="label">Related Entities:</span>
                      <div className="entities-list">
                        {getRelatedEntities(selectedNode.event).map(entity => (
                          <div key={entity.id} className="entity-item">
                            <span className="entity-icon">
                              {entity.type === 'faction' ? '⚔️' : entity.type === 'sanctum' ? '🏛️' : '👤'}
                            </span>
                            <span className="entity-name">{entity.name}</span>
                            <span className="entity-type">{entity.type}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="detail-row full-width">
                    <span className="label">Narrative Depth:</span>
                    <span className="value">{selectedNode.depth} levels from origin</span>
                  </div>

                  <div className="detail-row full-width">
                    <span className="label">Branches:</span>
                    <span className="value">{selectedNode.children.length} consequence{selectedNode.children.length !== 1 ? 's' : ''}</span>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
